import { css } from '@emotion/react';
import {
  Form,
  FormItem,
  FormElements,
  SelectInput,
  TextInput,
  Paragraph,
  colors,
  Button,
  DropInput,
  Avatar,
  Divider,
  Text,
} from 'common-components';
import { TSociodemographicCriteriaValues } from '@definitions';
import { useSociodemographicResources, useAppTranslation } from '@hooks';
import { useAuth } from 'react-oidc-context';
import { UserType } from '@types';
import { PropsWithChildren, useEffect } from 'react';
import { store } from '@store';

type UserPropsType = {
  user: UserType
};
const User = ({ user }: UserPropsType) => {
  const updateUserAction = store.use.updateUserAction().call;
  const updateUrlAvatarUserAction = store.use.updateUrlAvatarUserAction().call;
  const isLoadingUpdateUserAction = store.use.updateUserAction().status === 'LOADING';

  const [form] =
    FormElements.useForm<
      Pick<
        UserType,
        | 'idUser'
        | 'email'
        | 'phone'
        | 'lastName'
        | 'firstName'
        | 'nationality'
        | 'birthDate'
        | 'urlAvatar'
        | 'earning'
        | 'study'
        | 'family'
        | 'children'
        | 'job'
        | 'activitySector'
        | 'address'
      >
    >();
  const auth = useAuth();
  const { getResourceOptions } = useSociodemographicResources();
  const { translate } = useAppTranslation();

  useEffect(() => {
    if (user) {
      Object.keys(user).map((key) =>
        form.setFieldValue(
          key as Parameters<typeof form.setFieldValue>[0],
          user[key],
        ),
      );
    }
  }, [user]);

  const onUpdateConfirm = async () => {
    updateUserAction({
      idUser: user.idUser,
      phone: form.getFieldValue('phone'),
      lastName: form.getFieldValue('lastName'),
      firstName: form.getFieldValue('firstName'),
      nationality: form.getFieldValue('nationality'),
      birthDate: form.getFieldValue('birthDate'),
      earning: form.getFieldValue('earning'),
      study: form.getFieldValue('study'),
      family: form.getFieldValue('family'),
      children: form.getFieldValue('children'),
      job: form.getFieldValue('job'),
      activitySector: form.getFieldValue('activitySector'),
      address: form.getFieldValue('address'),
    });
  };

  const onAvatarUpload = async (file: File) => {
    updateUrlAvatarUserAction({ idUser: user.idUser, file });
  };

  const getSelectInputPropsByName = (
    name: Parameters<typeof form.setFieldValue>[0],
    resourceType: TSociodemographicCriteriaValues,
  ) => {
    return {
      onChange: (_, option) => form.setFieldValue(name, option),
      options: getResourceOptions(resourceType),
    };
  };

  const fixDateValue = (value: string | Date | number | null | undefined) => {
    let d: Date | null = null;

    if (!value) {
      d = null;
    } else if (value instanceof Date) {
      d = isNaN(value.getTime()) ? null : value;
    } else if (typeof value === 'number') {
      const tmp = new Date(value);
      d = isNaN(tmp.getTime()) ? null : tmp;
    } else if (typeof value === 'string') {
      const tmp = new Date(value);
      d = isNaN(tmp.getTime()) ? null : tmp;
    } else if (typeof (value as any)?.toDate === 'function') {
      try {
        const tmp = (value as any).toDate();
        d = isNaN(tmp.getTime()) ? null : tmp;
      } catch {
        d = null;
      }
    }

    // Résoud le problème d’affichage DD/MM/YYYY
    if (!d) {
      return null;
    }
    const display: Date = new Date(d.toLocaleDateString('fr-FR'));
    return display.toLocaleDateString();
  };

  return (
    <div css={$page}>
      <Text color={colors.gray900} style={{ fontSize: 24, lineHeight: '32px', fontWeight: 500 }}>
        {translate('user.title')}
      </Text>
      <FormSection
        title={translate('user.general.title')}
        description={translate('user.profileDescription')}
      >
        <Form form={form}>
          <FormItem name={'idUser'} label={translate('user.general.id')}>
            <TextInput disabled />
          </FormItem>
          <div css={$fieldsGrid}>
            <FormItem name={'email'} label={translate('user.general.email')}>
              <TextInput disabled />
            </FormItem>
            <FormItem name={'phone'} label={translate('user.general.phone')}>
              <TextInput />
            </FormItem>
            <FormItem name={'lastName'} label={translate('user.general.lastName')}>
              <TextInput />
            </FormItem>
            <FormItem name={'firstName'} label={translate('user.general.firstName')}>
              <TextInput />
            </FormItem>
            <FormItem
              name={'birthDate'}
              label={translate('user.general.birthDate')}
              getValueProps={(value) => ({
                value: fixDateValue(value),
              })}
            >
              <TextInput disabled />
            </FormItem>
            <FormItem name={'nationality'} label={translate('user.general.nationality')}>
              <SelectInput {...getSelectInputPropsByName('nationality', 'country')} />
            </FormItem>
          </div>
        </Form>
      </FormSection>
      <Divider css={$divider} />
      <FormSection
        title={translate('user.avatar.title')}
        description={translate('user.avatar.description')}
      >
        <div
          css={css`
            display: flex;
            gap: 20px;
            align-items: flex-start;
          `}
        >
          <Avatar size="2xl" src={user?.urlAvatar} style={{ flexShrink: 0, marginTop: 9 }} />
          <div
            css={css`
              width: 100%;
            `}
          >
            <DropInput
              beforeUpload={() => false}
              maxCount={1}
              onUpload={(ev) => onAvatarUpload(ev.fileList[0]?.originFileObj)}
            />
          </div>
        </div>
      </FormSection>
      <Divider css={$divider} />
      <FormSection
        title={translate('user.sociodemographic.title')}
        description={translate('user.profileDescription')}
      >
        <Form form={form}>
          <div css={$fieldsGrid}>
            <FormItem name={'earning'} label={translate('user.sociodemographic.earning')}>
              <SelectInput {...getSelectInputPropsByName('earning', 'earnings')} />
            </FormItem>
            <FormItem name={'study'} label={translate('user.sociodemographic.study')}>
              <SelectInput {...getSelectInputPropsByName('study', 'education')} />
            </FormItem>
            <FormItem name={'family'} label={translate('user.sociodemographic.family')}>
              <SelectInput {...getSelectInputPropsByName('family', 'maritalStatus')} />
            </FormItem>
            <FormItem name={'children'} label={translate('user.sociodemographic.children')}>
              <SelectInput {...getSelectInputPropsByName('children', 'children')} />
            </FormItem>
            <FormItem name={'job'} label={translate('user.sociodemographic.job')}>
              <SelectInput {...getSelectInputPropsByName('job', 'profession')} />
            </FormItem>
            <FormItem name={'activitySector'} label={translate('user.sociodemographic.activitySector')}>
              <SelectInput {...getSelectInputPropsByName('activitySector', 'activity')} />
            </FormItem>
          </div>
        </Form>
      </FormSection>
      <Divider css={$divider} />
      <FormSection
        title={translate('user.address.address')}
        description={translate('user.profileDescription')}
      >
        <Form form={form}>
          <FormItem name={['address', 'path']} label={translate('user.address.address')}>
            <TextInput />
          </FormItem>
          <div css={$fieldsGrid}>
            <FormItem name={['address', 'zipCode']} label={translate('user.address.zipCode')}>
              <TextInput />
            </FormItem>
            <FormItem name={['address', 'city']} label={translate('user.address.city')}>
              <TextInput />
            </FormItem>
            <FormItem name={['address', 'region']} label={translate('user.address.region')}>
              <SelectInput {...getSelectInputPropsByName(['address', 'region'], 'region')} />
            </FormItem>
            <FormItem name={['address', 'country']} label={translate('user.address.country')}>
              <SelectInput {...getSelectInputPropsByName(['address', 'country'], 'country')} />
            </FormItem>
          </div>
        </Form>
      </FormSection>
      <div
        css={css`
          display: flex;
          flex-direction: row-reverse;
          margin-top: 24px;
        `}
      >
        <Button size="sm" disabled={isLoadingUpdateUserAction} type="primary" onClick={onUpdateConfirm}>
          {translate('user.saveChanges')}
        </Button>
      </div>
      <div
        css={css`
          border: 1px solid ${colors.gray200};
          padding: 24px;
          margin: 24px 0;
          border-radius: 12px;
        `}
      >
        <Paragraph preset="panelCard">{translate('user.logout.label')}</Paragraph>
        <Paragraph>{translate('user.logout.description')}</Paragraph>
        <Button
          css={css`
            border: 1px solid #d0d5dd;
            box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
            border-radius: 8px;
          `}
          size="sm"
          type="outline"
          iconLeft="LogOut01"
          onClick={async () => await auth.signoutRedirect()}
        >
          {translate('user.logout.label')}
        </Button>
      </div>
    </div>
  );
};

type FormSectionPropsType = PropsWithChildren<{
  title: string
  description: string
}>;

const FormSection = ({ title, description, children }: FormSectionPropsType) => (
  <div
    css={css`
      display: grid;
      grid-template-columns: 280px 1fr;
      gap: 32px;
    `}
  >
    <div>
      <Text color={colors.gray700} style={{ display: 'block', fontWeight: 500 }}>
        {title}
      </Text>
      <Text color={colors.gray600} style={{ display: 'block' }}>
        {description}
      </Text>
    </div>
    {children}
  </div>
);

const $page = css`
  display: flex;
  flex-direction: column;
  padding-top: 31px;

  & > .ant-typography:first-of-type {
    margin-bottom: 31px;
  }

  .ant-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label {
    padding-bottom: 6px !important;
  }

  .ant-form-item-label > label {
    font-weight: 500;
    color: ${colors.gray700};
  }
`;

const $fieldsGrid = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const $divider = css`
  margin: 24px 0 !important;
  border-color: ${colors.gray200} !important;
`;

export default User;
